import { useParams, Navigate, Link } from 'react-router-dom';
import { SEO } from '../../../components/seo/SEO';
import { calculateReadingTime, getBaseUrl, optimizeImageUrl } from '../../../lib/utils';
import { useMemo, useState, useEffect } from 'react';
import { supabase, Post } from '../../../lib/supabase';
import { replaceImageSlots } from '../../../lib/imageSlots';
import { PostHeader } from './components/PostHeader';
import { PostSummaryBox } from './components/PostSummaryBox';
import { PostTOC } from './components/PostTOC';
import { PostRelated } from './components/PostRelated';
import { PostShareBar } from './components/PostShareBar';
import TTSControls from '../../../components/tts/TTSControls';

const defaultSummaryItems = [
  'AIツールを実際に使った制作フローの全体像',
  '詰まったポイントと、その解決策の具体的な手順',
  '再現できる形で整理されたプロセスとテンプレート',
];

export default function PostDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [allPosts, setAllPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) return;
      try {
        const [postRes, allRes] = await Promise.all([
          supabase.from('posts').select('*').eq('slug', slug).maybeSingle(),
          supabase.from('posts').select('*').lte('published_at', new Date().toISOString()).order('published_at', { ascending: false }).limit(10),
        ]);
        if (postRes.error) throw postRes.error;
        setPost(postRes.data);
        setAllPosts(allRes.data || []);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const processedContent = useMemo(() => {
    if (!post) return '';
    const imageMap = post.image_map
      ? Object.fromEntries(
          Object.entries(post.image_map).map(([slotName, val]) => [
            slotName,
            { slotName, url: val.url, alt: val.alt },
          ])
        )
      : {};
    return replaceImageSlots(post.content || '', imageMap);
  }, [post]);

  const plainText = useMemo(() => {
    if (!post) return '';
    const bodyText = (post.content || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    return `${post.title}\n\n${post.excerpt}\n\n${bodyText}`;
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFAF6] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-3xl text-[#9A8E84] animate-spin block mb-3"></i>
          <p className="text-sm text-[#9A8E84]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            読み込み中...
          </p>
        </div>
      </div>
    );
  }

  if (!post) {
    return <Navigate to="/404" replace />;
  }

  const baseUrl = getBaseUrl();
  const readingTime = calculateReadingTime(post);

  const seoTitle = (() => {
    const maxLen = 52;
    if (post.title.length <= maxLen) return post.title;
    const truncated = post.title.slice(0, maxLen);
    const lastBreak = Math.max(
      truncated.lastIndexOf('。'),
      truncated.lastIndexOf('、'),
      truncated.lastIndexOf(' '),
    );
    return lastBreak > maxLen * 0.6 ? truncated.slice(0, lastBreak + 1) : truncated + '…';
  })();

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.published_at,
    dateModified: post.updated_at,
    author: { '@type': 'Person', name: post.author_name || 'つくってなんぼ編集部' },
    publisher: {
      '@type': 'Organization',
      name: 'つくってなんぼ',
      logo: { '@type': 'ImageObject', url: `${baseUrl}/og-image.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${baseUrl}/articles/${post.slug}` },
    image: post.image_url || `${baseUrl}/og-image.png`,
    keywords: post.tags.join(', '),
  };

  return (
    <>
      <SEO
        title={`${seoTitle} | つくってなんぼ`}
        description={post.excerpt}
        canonical={`/articles/${post.slug}`}
        ogType="article"
        ogImage={post.image_url}
        jsonLd={jsonLd}
        article={{
          publishedTime: post.published_at,
          modifiedTime: post.updated_at,
          tags: post.tags,
        }}
      />

      <div className="min-h-screen bg-[#FDFAF6]">
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14">

          {/* Article header */}
          <PostHeader post={post} />

          {/* Cover image */}
          {post.image_url && (
            <div className="w-full rounded-lg overflow-hidden mb-8 sm:mb-10" style={{ aspectRatio: '16/9' }}>
              <img
                src={optimizeImageUrl(post.image_url, { width: 900, quality: 80 })}
                alt={post.title}
                loading="eager"
                decoding="async"
                className="w-full h-full object-cover object-top"
              />
            </div>
          )}

          {/* TTS */}
          <div className="flex items-center gap-2 mb-6 text-xs text-[#9A8E84]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            <i className="ri-volume-up-line text-[#5B7B6F]"></i>
            <TTSControls />
          </div>

          {/* Summary box */}
          <PostSummaryBox items={defaultSummaryItems} />

          {/* TOC */}
          <PostTOC content={processedContent} />

          {/* CTA block */}
          <div className="my-8 bg-[#F0EBE3] border border-[#D4C9BE] rounded-lg px-5 py-4 flex items-start gap-3">
            <i className="ri-lightbulb-line text-[#5B7B6F] text-base mt-0.5 flex-shrink-0"></i>
            <div>
              <p
                className="text-sm font-semibold text-[#2C2416] mb-1"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                制作フロー全体を見たい方へ
              </p>
              <p
                className="text-xs text-[#7A6E62] leading-relaxed mb-2"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                このサイトの制作記録シリーズでは、仕様決め・デザイン・実装・SEOまで一通りの流れを残しています。
              </p>
              <Link
                to="/categories/build-log"
                className="inline-flex items-center gap-1 text-xs text-[#5B7B6F] font-semibold hover:text-[#2C2416] transition-colors cursor-pointer"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                制作記録を読む
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          {/* Article body */}
          <article
            className="prose-editorial"
            dangerouslySetInnerHTML={{ __html: processedContent }}
          />

          {/* Share bar */}
          <PostShareBar post={post} plainText={plainText} />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-10">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-[#7A6E62] bg-[#F0EBE3] border border-[#E8E0D5] px-3 py-1 rounded-full"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Related articles */}
          <PostRelated currentPost={post} allPosts={allPosts} />

          {/* Back to top */}
          <div className="mt-12 pt-8 border-t border-[#EDE7DF] text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-[#7A6E62] hover:text-[#2C2416] transition-colors cursor-pointer"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              <i className="ri-arrow-left-line"></i>
              ホームに戻る
            </Link>
          </div>

        </main>
      </div>
    </>
  );
}
